import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FOCUS_LIMIT } from '@cerebro-diem/core';
import { useFocusChooser } from '../services/focusChooser';
import { errorMessage } from '../services/errors';
import { queryKeys } from '../services/queries';
import { supabase } from '../services/supabase';
import { saveTaskEdit } from '../services/tasks';
import { dialogBackdrop, dialogPanel, outlineButton } from './styles';

/**
 * "Focus holds 3: which one makes room?" (CER-135), over whatever page a task
 * was focused from. Choosing one takes it out of focus and puts the new one in.
 */
export default function FocusChooser() {
  const asking = useFocusChooser((state) => state.asking);
  const finish = useFocusChooser((state) => state.finish);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const focused = useQuery({
    queryKey: ['focusFull', asking?.date, asking?.taskId],
    enabled: asking !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, name')
        .eq('focus_date', asking?.date as string)
        .neq('status', 'done')
        .neq('id', asking?.taskId as string)
        .order('name');
      if (error) throw error;
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  useEffect(() => {
    if (!asking) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asking, finish]);

  if (!asking) return null;
  const close = (made: boolean) => {
    setFailure(null);
    finish(made);
  };
  const makeRoom = async (id: string) => {
    setBusy(true);
    setFailure(null);
    try {
      await saveTaskEdit(id, { focus_date: null });
      await saveTaskEdit(asking.taskId, { focus_date: asking.date });
      await queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      close(true);
    } catch (error) {
      setFailure(`Not changed: ${errorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={dialogBackdrop} onClick={() => close(false)}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-full-title"
        className={dialogPanel}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="focus-full-title" className="text-lg font-semibold mb-2">
          Focus holds {FOCUS_LIMIT}
        </h2>
        <p className="text-sm text-on-surface-variant mb-4">
          What do you really need to focus on? Take one out to make room for "{asking.taskName}".
        </p>
        {focused.isError && <p className="text-sm text-error mb-2">Your focus could not be read: {errorMessage(focused.error)}</p>}
        <div className="flex flex-col gap-2 mb-4">
          {(focused.data ?? []).map((task) => (
            <button key={task.id} type="button" disabled={busy} onClick={() => void makeRoom(task.id)} className={outlineButton}>
              Take “{task.name}” out of focus
            </button>
          ))}
        </div>
        {failure && <p className="text-sm text-error mb-2">{failure}</p>}
        <button type="button" disabled={busy} onClick={() => close(false)} className={outlineButton}>
          Keep these
        </button>
      </div>
    </div>
  );
}
